import React, { useMemo, useState } from "react";
import { gql } from "@apollo/client";
import { useOrgGuestFeedbackSyncsSidebarQuery } from "./GuestFeedbackSyncDetailSidebar.generated";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import ArrowsRefreshIcon from "@src/lib/icons/18px/arrows-refresh";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@src/lib/ui/Tabs";
import {
  HtmlTable,
  HtmlTableBody,
  HtmlTableCell,
  HtmlTableHead,
  HtmlTableHeader,
  HtmlTableRow,
} from "@src/lib/ui/HtmlTable";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { cn } from "@src/lib/utils/cn";
import { formatDateTime } from "@src/lib/utils/dates";
import { format } from "date-fns";

const StarRating: React.FC<{ rating: number | null | undefined }> = ({
  rating,
}) => {
  if (rating == null)
    return (
      <Text variant="fine-print-description" className="text-muted-foreground">
        —
      </Text>
    );
  const rounded = Math.round(rating);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={cn(
            "text-sm",
            i < rounded ? "text-yellow-7" : "text-gray-3",
          )}
        >
          ★
        </span>
      ))}
      <Text
        variant="fine-print-description"
        className="ml-1 text-muted-foreground"
      >
        {rating.toFixed(1)}
      </Text>
    </div>
  );
};

const PAGE_SIZE = 25;

type Props = {
  rowId: string;
  showHeader?: boolean;
};

gql`
  query OrgGuestFeedbackSyncsSidebar {
    OrgGuestFeedbackSyncs {
      objects {
        id
        startedAt
        completedAt
        feedbacks {
          id
          text
          rating
          date
          integrationLocation {
            id
            sourceName
          }
          csvUploadLocation {
            id
            locationName
          }
        }
        orgGuestFeedbackIntegration {
          id
          googleBusinessConfig {
            id
          }
          ovationIntegrationConfig {
            id
          }
          tattleIntegrationConfig {
            id
          }
        }
      }
    }
  }
`;

type SyncObject = NonNullable<
  NonNullable<
    ReturnType<typeof useOrgGuestFeedbackSyncsSidebarQuery>["data"]
  >["OrgGuestFeedbackSyncs"]
>["objects"][number];

const getIntegrationType = (sync: SyncObject): string => {
  const integration = sync.orgGuestFeedbackIntegration;
  if (integration.googleBusinessConfig) {
    return "Google Business";
  }
  if (integration.ovationIntegrationConfig) {
    return "Ovation";
  }
  if (integration.tattleIntegrationConfig) {
    return "Tattle";
  }
  return "Unknown";
};

const GuestFeedbackSyncDetailSidebar: React.FC<Props> = ({
  rowId,
  showHeader = true,
}) => {
  const [feedbackPage, setFeedbackPage] = useState(0);

  const { data } = useOrgGuestFeedbackSyncsSidebarQuery({
    fetchPolicy: "cache-first",
    skip: !rowId,
  });

  const sync = useMemo(() => {
    return (
      data?.OrgGuestFeedbackSyncs?.objects?.find((s) => s.id === rowId) ?? null
    );
  }, [data, rowId]);

  if (!sync) return null;

  const isCompleted = !!sync.completedAt;
  const feedbacksCount = sync.feedbacks?.length ?? 0;
  const totalPages = Math.ceil(feedbacksCount / PAGE_SIZE);
  const paginatedFeedbacks =
    sync.feedbacks?.slice(
      feedbackPage * PAGE_SIZE,
      (feedbackPage + 1) * PAGE_SIZE,
    ) ?? [];

  const detailsItems: InfoTableItem[] = [
    {
      label: "Sync ID",
      value: sync.id,
    },
    {
      label: "Integration",
      value: getIntegrationType(sync),
    },
    {
      label: "Integration ID",
      value: sync.orgGuestFeedbackIntegration.id,
    },
    {
      label: "Started",
      value: formatDateTime(new Date(sync.startedAt)),
    },
    {
      label: "Completed",
      value: sync.completedAt
        ? formatDateTime(new Date(sync.completedAt))
        : "--",
    },
    {
      label: "Status",
      value: isCompleted ? "Completed" : "In Progress",
    },
    {
      label: "Reviews",
      value: String(feedbacksCount),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title="Integration Sync"
        icon={<ArrowsRefreshIcon className="size-[18px] text-gray-9" />}
        sidebarType={DetailSidebarType.GuestFeedbackSync}
      />
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <Tabs size="sm" defaultValue="details">
            <div className="flex border-b border-border-primary">
              <TabsList fill aria-label="Sync">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger
                  value="feedbacks"
                  count={feedbacksCount || undefined}
                >
                  Reviews
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="details" className="mt-3">
              <InfoTable items={detailsItems} />
            </TabsContent>
            <TabsContent value="feedbacks" className="mt-3">
              {!sync.feedbacks || sync.feedbacks.length === 0 ? (
                <div className="p-4">
                  <div className="text-gray-7">No reviews in this sync</div>
                </div>
              ) : (
                <div className="p-4">
                  <HtmlTable>
                    <HtmlTableHeader>
                      <HtmlTableRow>
                        <HtmlTableHead className="w-[100px]">
                          Date
                        </HtmlTableHead>
                        <HtmlTableHead className="w-[120px]">
                          Rating
                        </HtmlTableHead>
                        <HtmlTableHead className="w-[140px]">
                          Location
                        </HtmlTableHead>
                        <HtmlTableHead>Review</HtmlTableHead>
                      </HtmlTableRow>
                    </HtmlTableHeader>
                    <HtmlTableBody>
                      {paginatedFeedbacks.map((feedback) => (
                        <HtmlTableRow key={feedback.id}>
                          <HtmlTableCell>
                            <Text
                              variant="fine-print-description"
                              className="text-muted-foreground"
                            >
                              {feedback.date
                                ? format(new Date(feedback.date), "MMM d, yyyy")
                                : "—"}
                            </Text>
                          </HtmlTableCell>
                          <HtmlTableCell>
                            <StarRating rating={feedback.rating} />
                          </HtmlTableCell>
                          <HtmlTableCell>
                            <Text
                              variant="fine-print-description"
                              className="text-muted-foreground"
                            >
                              {feedback.integrationLocation?.sourceName ??
                                feedback.csvUploadLocation?.locationName ??
                                "—"}
                            </Text>
                          </HtmlTableCell>
                          <HtmlTableCell>
                            <Text
                              variant="compact-description"
                              className="line-clamp-2"
                            >
                              {feedback.text ?? "—"}
                            </Text>
                          </HtmlTableCell>
                        </HtmlTableRow>
                      ))}
                    </HtmlTableBody>
                  </HtmlTable>
                  {totalPages > 1 && (
                    <div className="mt-3 flex items-center justify-between">
                      <Text
                        variant="fine-print-description"
                        className="text-muted-foreground"
                      >
                        Page {feedbackPage + 1} of {totalPages}
                      </Text>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={feedbackPage === 0}
                          onClick={() => setFeedbackPage((p) => p - 1)}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={feedbackPage >= totalPages - 1}
                          onClick={() => setFeedbackPage((p) => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SidebarContent>
    </div>
  );
};

export default GuestFeedbackSyncDetailSidebar;
